import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Shield, ExternalLink, MapPin, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Subprocessor {
  name: string;
  purpose: string;
  dataProcessed: string[];
  location: string;
  safeguards: string;
  dpaUrl?: string;
}

const subprocessors: Subprocessor[] = [
  {
    name: "Supabase Inc.",
    purpose: "Database hosting, authentication, and real-time data sync",
    dataProcessed: ["Account data", "User content", "Session data", "Authentication tokens"],
    location: "United States (AWS)",
    safeguards: "Standard Contractual Clauses (SCCs), SOC 2 Type II",
    dpaUrl: "https://supabase.com/legal/dpa"
  },
  {
    name: "Anthropic PBC",
    purpose: "AI-powered therapeutic conversations (Claude)",
    dataProcessed: ["Chat messages (anonymised)", "Session context"],
    location: "United States",
    safeguards: "Standard Contractual Clauses (SCCs), Zero data retention policy",
    dpaUrl: "https://www.anthropic.com/legal/commercial-terms"
  },
  {
    name: "OpenAI LLC",
    purpose: "AI-powered dream analysis and guidance",
    dataProcessed: ["Dream descriptions (anonymised)", "Analysis requests"],
    location: "United States",
    safeguards: "Standard Contractual Clauses (SCCs), API data not used for training",
    dpaUrl: "https://openai.com/policies/data-processing-agreement"
  },
  {
    name: "ElevenLabs Inc.",
    purpose: "Voice synthesis for audio features and sound effects",
    dataProcessed: ["Text prompts for audio generation", "Voice preference settings"],
    location: "United States",
    safeguards: "Standard Contractual Clauses (SCCs), GDPR compliant, DPA on request",
    dpaUrl: "https://elevenlabs.io/terms-of-service"
  },
  {
    name: "Stripe Inc.",
    purpose: "Payment processing and subscription management",
    dataProcessed: ["Payment card details", "Billing address", "Transaction history"],
    location: "United States",
    safeguards: "PCI-DSS Level 1, Standard Contractual Clauses (SCCs)",
    dpaUrl: "https://stripe.com/legal/dpa"
  },
  {
    name: "WHOOP Inc.",
    purpose: "Wearable health data integration (optional)",
    dataProcessed: ["Sleep metrics", "Recovery scores", "Heart rate data"],
    location: "United States",
    safeguards: "User-initiated OAuth connection, data deleted on disconnect",
    dpaUrl: "https://www.whoop.com/privacy/"
  },
  {
    name: "Garmin Ltd.",
    purpose: "Fitness tracker integration (optional)",
    dataProcessed: ["Activity data", "Sleep data", "Heart rate"],
    location: "United States / Switzerland",
    safeguards: "User-initiated OAuth connection, data deleted on disconnect",
    dpaUrl: "https://www.garmin.com/legal/privacy/"
  },
  {
    name: "Lovable (GPT Engineer AB)",
    purpose: "Application hosting and deployment",
    dataProcessed: ["Application code", "Static assets"],
    location: "European Union",
    safeguards: "GDPR-compliant hosting within EU",
    dpaUrl: "https://lovable.dev/legal/dpa"
  }
];

const Subprocessors = () => {
  return (
    <>
      <Helmet>
        <title>Sub-processors | Therappi</title>
        <meta name="description" content="Complete list of third-party service providers that process data on behalf of Therappi AI. Transparency about our data processing chain." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Sub-processors</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              This page lists all third-party service providers that process personal data on behalf of Therappi AI. 
              We maintain transparency about our data processing chain to help you understand how your information is handled.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: December 2024
            </p>
          </div>

          {/* What is a sub-processor */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                What is a Sub-processor?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                A sub-processor is a third-party service provider that processes personal data on our behalf to help deliver our services. 
                Under GDPR Article 28, we are required to inform you about these processors and ensure they maintain appropriate data protection standards.
              </p>
              <p className="mt-3">
                All sub-processors listed below have signed Data Processing Agreements (DPAs) with appropriate safeguards for international data transfers, 
                including Standard Contractual Clauses (SCCs) where required.
              </p>
            </CardContent>
          </Card>

          {/* Sub-processor table */}
          <Card>
            <CardHeader>
              <CardTitle>Current Sub-processors</CardTitle>
              <CardDescription>
                Complete list of third-party processors authorised to handle data on behalf of Therappi
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[150px]">Provider</TableHead>
                      <TableHead className="min-w-[200px]">Purpose</TableHead>
                      <TableHead className="min-w-[200px]">Data Processed</TableHead>
                      <TableHead className="min-w-[120px]">Location</TableHead>
                      <TableHead className="min-w-[180px]">Safeguards</TableHead>
                      <TableHead className="w-[100px]">DPA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subprocessors.map((processor) => (
                      <TableRow key={processor.name}>
                        <TableCell className="font-medium">{processor.name}</TableCell>
                        <TableCell className="text-muted-foreground">{processor.purpose}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {processor.dataProcessed.map((data) => (
                              <Badge key={data} variant="secondary" className="text-xs">
                                {data}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="text-sm">{processor.location}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{processor.safeguards}</TableCell>
                        <TableCell>
                          {processor.dpaUrl && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={processor.dpaUrl} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Changes notification */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Notification of Changes</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                We will update this page when we add or remove sub-processors. Material changes will be communicated via:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-1">
                <li>Email notification to registered users</li>
                <li>In-app notification upon next login</li>
                <li>Update to this sub-processors page</li>
              </ul>
              <p className="mt-4">
                If you have concerns about any sub-processor, please contact us at{" "}
                <a href="mailto:privacy@therappi.app" className="text-primary hover:underline">
                  privacy@therappi.app
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Back link */}
          <div className="mt-8 text-center">
            <Button variant="outline" asChild>
              <a href="/privacy">← Back to Privacy Policy</a>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Subprocessors;
